import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { clientInventoryAPI } from '../../services/api';
import UsageDialog from '../../components/dialogs/UsageDialog';
import DiscardDialog from '../../components/dialogs/DiscardDialog';
import ViewDetailsDialog from '../../components/dialogs/ViewDetailsDialog';

const InventoryManagement = ({ currentUser }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0=All, 1=Unused, 2=Used/Discarded
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    unused: 0,
    used: 0,
    discarded: 0,
    expiringWithin30Days: 0,
    expiringWithin60Days: 0
  });

  // Dialog state
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  
  // Action menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Map tab index to status filter
  const getStatusFilter = (tabIndex) => {
    switch (tabIndex) {
      case 0: return 'ALL';
      case 1: return 'UNUSED';
      case 2: return 'USED_DISCARDED';
      default: return 'ALL';
    }
  };

  // Fetch inventory data
  const fetchInventory = async () => {
    try {
      setLoading(true);
      setError('');
      
      const statusFilter = getStatusFilter(activeTab);
      
      const response = await clientInventoryAPI.getInventory({
        status: statusFilter,
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
        orderNumber: searchTerm || undefined
      });
      
      if (response.success) {
        setInventory(response.data);
        setTotalCount(response.pagination.total);
      } else {
        setError(response.error || 'Failed to load inventory');
      }
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await clientInventoryAPI.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    fetchInventory();
  }, [activeTab, page, rowsPerPage]);

  // Load stats on mount
  useEffect(() => {
    fetchStats();
  }, []);

  // Search handler with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 0) {
        fetchInventory();
      } else {
        setPage(0); // Reset to first page, which will trigger fetch
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setMenuItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuItem(null);
  };

  const handleMarkUsed = (item) => {
    handleCloseMenu();
    setSelectedItem(item);
    setDialogMode('create');
    setUsageDialogOpen(true);
  };

  const handleMarkDiscarded = (item) => {
    handleCloseMenu();
    setSelectedItem(item);
    setDialogMode('create');
    setDiscardDialogOpen(true);
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setDetailsDialogOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setDialogMode('edit');
    if (item.status === 'USED') {
      setUsageDialogOpen(true);
    } else if (item.status === 'DISCARDED') {
      setDiscardDialogOpen(true);
    }
  };

  const handleUsageSuccess = () => {
    setUsageDialogOpen(false);
    setSelectedItem(null);
    fetchInventory();
    fetchStats();
    setSnackbar({
      open: true,
      message: dialogMode === 'edit' ? 'Usage information updated successfully' : 'Tissue ID marked as used successfully',
      severity: 'success'
    });
  };

  const handleDiscardSuccess = () => {
    setDiscardDialogOpen(false);
    setSelectedItem(null);
    fetchInventory();
    fetchStats();
    setSnackbar({
      open: true,
      message: dialogMode === 'edit' ? 'Discard information updated successfully' : 'Tissue ID marked as discarded successfully',
      severity: 'success'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'UNUSED':
        return 'success';
      case 'USED':
        return 'primary';
      case 'DISCARDED':
        return 'default';
      default:
        return 'default';
    }
  };

  // Check if expiring soon
  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const exp = new Date(expirationDate);
    const now = new Date();
    const daysUntilExpiry = Math.floor((exp - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <InventoryIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Inventory Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track all vials shipped to you and log usage information
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total Vials
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Unused
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {stats.unused}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Used
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {stats.used}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Discarded
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.secondary">
                {stats.discarded}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Expiration Warnings */}
      {(stats.expiringWithin30Days > 0 || stats.expiringWithin60Days > 0) && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <Typography variant="body2">
            <strong>Expiration Alert:</strong> You have {stats.expiringWithin30Days} vial(s) expiring within 30 days
            {stats.expiringWithin60Days > stats.expiringWithin30Days && ` and ${stats.expiringWithin60Days - stats.expiringWithin30Days} more expiring within 60 days`}.
          </Typography>
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by Tissue ID or Order Number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={`All (${stats.total})`} />
          <Tab label={`Unused (${stats.unused})`} />
          <Tab label={`Used/Discarded (${stats.used + stats.discarded})`} />
        </Tabs>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tissue ID</TableCell>
                {!isMobile && <TableCell>Order #</TableCell>}
                <TableCell>Order Date</TableCell>
                {!isMobile && <TableCell>Product</TableCell>}
                {!isMobile && <TableCell>Exp. Date</TableCell>}
                <TableCell>Status</TableCell>
                {!isMobile && activeTab === 2 && <TableCell>Date Used/Disc</TableCell>}
                {!isMobile && activeTab === 2 && <TableCell>Horse Name</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : inventory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No inventory items found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inventory.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Tooltip title={item.tissueId}>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            maxWidth: isMobile ? 100 : 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.tissueId}
                        </Typography>
                      </Tooltip>
                      {isExpiringSoon(item.expirationDate) && (
                        <Chip
                          label="Expiring Soon"
                          size="small"
                          color="warning"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    {!isMobile && <TableCell>{item.orderNumber}</TableCell>}
                    <TableCell>{formatDate(item.orderDate)}</TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {item.productName}
                        </Typography>
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={isExpiringSoon(item.expirationDate) ? 'warning.main' : 'text.primary'}
                        >
                          {formatDate(item.expirationDate)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={getStatusColor(item.status)}
                        size="small"
                      />
                    </TableCell>
                    {!isMobile && activeTab === 2 && (
                      <TableCell>
                        {formatDate(item.dateUsed || item.dateDiscarded)}
                      </TableCell>
                    )}
                    {!isMobile && activeTab === 2 && (
                      <TableCell>{item.horseName || '-'}</TableCell>
                    )}
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(item)}
                            color="primary"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {item.status === 'UNUSED' ? (
                          <Tooltip title="Mark as Used/Discarded">
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, item)}
                              color="primary"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Dialogs */}
      <UsageDialog
        open={usageDialogOpen}
        onClose={() => {
          setUsageDialogOpen(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
        mode={dialogMode}
        onSuccess={handleUsageSuccess}
      />

      <DiscardDialog
        open={discardDialogOpen}
        onClose={() => {
          setDiscardDialogOpen(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
        mode={dialogMode}
        onSuccess={handleDiscardSuccess}
      />

      <ViewDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedItem(null);
        }}
        tissueId={selectedItem?.tissueId}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Action Menu for UNUSED items */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleMarkUsed(menuItem)}>
          <ListItemIcon>
            <CheckCircleIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Mark as Used</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMarkDiscarded(menuItem)}>
          <ListItemIcon>
            <CancelIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Mark as Discarded</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default InventoryManagement;

